<template>
  <v-form v-model="valid" ref="form">
    <v-radio-group v-model="localValue.metadata.allRecipients" hide-details>
      <v-radio
        class="body2 mb-1"
        data-testid="all-recipients-true"
        :label="$t('modals.allowBlockEmail.recipientsActions.applyAll')"
        :value="true"
        :ripple="false"
      />
      <v-radio
        class="body2"
        data-testid="all-recipients-false"
        :label="$t('modals.allowBlockEmail.recipientsActions.applySpecific')"
        :value="false"
        :ripple="false"
      />
    </v-radio-group>
    <v-autocomplete
      v-if="!localValue.metadata.allRecipients"
      v-model="localValue.metadata.recipients"
      data-testid="recipients-combobox"
      class="mt-2 coro-scrollable-combobox"
      :items="localValue.item?.sections?.emailMetaData?.proxyBlockedRecipients"
      :label="$t('modals.allowBlockEmail.recipientsActions.label')"
      :placeholder="$t('modals.allowBlockEmail.recipientsActions.placeholder')"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      multiple
      hide-no-data
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :text="item.title"
          color="primary"
        >
          <div class="d-flex align-center">
            {{ item.raw }}
          </div>
        </v-chip>
      </template>
      <template #item="{ props, item }">
        <v-list-item v-bind="props" title="">
          <div class="d-flex align-center" :data-testid="`item-${camelCase(item.raw)}`">
            <span class="ml-1">{{ item.raw }}</span>
          </div>
        </v-list-item>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, watch } from "vue";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { TicketAction } from "@/constants/tickets";
import { atLeastOneIsRequired } from "@/_helpers/validators.ts";
import camelCase from "lodash/camelCase";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<
        DialogDataConfig<
          TicketDetails & {
            closeTicket?: boolean;
            allowAction: boolean;
          },
          TicketAction
        >
      >,
      required: true,
    },
  },
  setup(props, { emit }) {
    const valid = ref<boolean>(false);
    const form = ref();
    const localValue = ref({
      ...props.config,
      action: TicketAction.APPROVE_EMAIL,
      metadata: {
        allRecipients: true,
        recipients: [],
      },
    });

    watch(
      localValue,
      (newValue) => {
        emit("update:localValue", { ...newValue, item: { ...newValue.item } });
      },
      { deep: true, immediate: true }
    );

    watch(
      () => valid.value,
      (newVal) => {
        if (newVal === null) {
          return;
        }
        emit("update:valid", newVal);
      }
    );

    const validate = async () => {
      const { valid } = await form.value.validate();
      return valid;
    };

    return {
      localValue,
      atLeastOneIsRequired,
      camelCase,
      valid,
      form,
      validate,
    };
  },
});
</script>
